import { Router as NativeRouter, Route as NativeRoute } from 'preact-iso';
import { Children, isValidElement } from 'react';
import { sortRoutesByWeight } from './route-comparator.js';

/**
 * Wraps preact-iso {@link NativeRouter} and expands Route children so that array `path` values
 * become multiple native routes. This is required because preact-iso Router matches
 * `vnode.props.path` directly and does not support array paths natively.
 *
 * @function Router
 *
 * @param {object} props - Props forwarded to the underlying preact-iso Router.
 * @param {Array|object} props.children - Route child elements. Each child may specify a `path` prop as a string or an array of strings.
 *
 * @returns {object} A preact-iso Router element with expanded route children.
 * @memberof Mojito.Applications.Common
 */
export default function Router(props) {
    const { children, ...rest } = props;
    const expandedRotes = parseRouteChildren(children);
    return <NativeRouter {...rest}>{sortRoutesByWeight(expandedRotes)}</NativeRouter>;
}

/**
 * Flattens Route children: when a child's `path` prop is an array, a separate
 * {@link NativeRoute} element is created for each path entry. This allows defining
 * multiple navigation types pointing to the same route component.
 *
 * @function parseRouteChildren
 *
 * @param {Array} routeChildren - React children from Router.
 *
 * @returns {Array} Expanded list of NativeRoute elements.
 * @private
 * @memberof Mojito.Applications.Common
 */
const parseRouteChildren = routeChildren => {
    return Children.toArray(routeChildren).flatMap(child => {
        if (!isValidElement(child)) {
            return [child];
        }
        const { path, ...rest } = child.props;
        if (Array.isArray(path)) {
            // eslint-disable-next-line react/jsx-key
            return path.flat().map(p => <NativeRoute path={p} {...rest} />);
        }
        // eslint-disable-next-line react/jsx-key
        return [<NativeRoute path={path} {...rest} />];
    });
};
